import { useState } from "react";
import { post } from "@common/api/apiShared.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useSelectableBusinessAreaByUserAccessQuery, useSelectableBusinessAreaQuery } from "@common/hooks/useSelectableItemQueries";
import { TransferContractAccessBusinessAreaModel } from "./TransferContractAccessBusinessAreaDialog.types";

export interface ITransferContractAccessBusinessAreaDialogHooksProps {
    onDialogClose: () => void;
}

function useTransferContractAccessBusinessAreaDialogHooks(props: ITransferContractAccessBusinessAreaDialogHooksProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const [transferModel, setTransferModel] = useState<TransferContractAccessBusinessAreaModel>({
        fromBusinessAreaId: null,
        toBusinessAreaId: null,
    });

    const businessAreaQuery = useSelectableBusinessAreaQuery();

    const saveMutation = useDotLegalMutation(save);
    const transfer = async () => {
        await saveMutation.mutateAsync(transferModel, {
            onSuccess: () => {
                props.onDialogClose();
                snackbar.show(translateString("contractAccessBusinessAreaTransferred"));
            },
        });
    };

    let fromBusinessAreas = businessAreaQuery.data?.filter((x) => x.id !== transferModel.toBusinessAreaId).sort((a, b) => a.name.localeCompare(b.name));
    let toBusinessAreas = businessAreaQuery.data?.filter((x) => x.id !== transferModel.fromBusinessAreaId).sort((a, b) => a.name.localeCompare(b.name));

    const onFromBusinessAreaChange = (fromBusinessAreaId: string | null) => {
        let temp = { ...transferModel };
        temp.fromBusinessAreaId = fromBusinessAreaId;
        setTransferModel(temp);
    };

    const onToBusinessAreaChange = (toBusinessAreaId: string | null) => {
        let temp = { ...transferModel };
        temp.toBusinessAreaId = toBusinessAreaId;
        setTransferModel(temp);
    };

    const transferButtonEnabled = transferModel.fromBusinessAreaId !== null && transferModel.toBusinessAreaId !== null;

    return {
        fromBusinessAreas,
        toBusinessAreas,
        transferModel,
        onFromBusinessAreaChange,
        onToBusinessAreaChange,
        transfer,
        transferButtonEnabled,
        inProgress: saveMutation.isLoading,
        isLoading: businessAreaQuery.isLoading,
    };

    function save(model: TransferContractAccessBusinessAreaModel) {
        return post<{}>("/administrative-functions/transfer-contract-access-business-area", model);
    }
}

export default useTransferContractAccessBusinessAreaDialogHooks;
